import { TAILWIND_COLORS } from "#src/utils/common";
import { Typography } from "@lockerpm/design";
import { ReactNode, useMemo } from "react";
import { badgeColors, tagColorClasses, tailwindColorClasses } from "./utils";
import { Tag01, XClose } from "@untitled-ui/icons-react";

const sizes = ["sm", "md", "lg"] as const;

const getLeftPadding = (
  size: (typeof sizes)[number],
  leftIcon: "default" | "icon" | null
) => {
  if (leftIcon === "default") {
    if (size === "sm") return "pl-1.5";
    if (size === "md") return "pl-2";
    if (size === "lg") return "pl-2.5";
  }

  if (leftIcon === "icon") {
    if (size === "sm") return "pl-[0.1875rem]";
    if (size === "md") return "pl-1";
    if (size === "lg") return "pl-1";
  }

  if (size === "sm") return "pl-2";
  if (size === "md") return "pl-2.5";
  if (size === "lg") return "pl-3";

  return "pl-2";
};

const getRightPadding = (
  size: (typeof sizes)[number],
  rightIcon: "default" | "icon" | null
) => {
  if (rightIcon === "default") {
    if (size === "sm") return "pr-1.5";
    if (size === "md") return "pr-2";
    if (size === "lg") return "pr-2.5";
  }

  if (rightIcon === "icon") {
    if (size === "sm") return "pr-[0.1875rem]";
    if (size === "md") return "pr-1";
    if (size === "lg") return "pr-1";
  }

  if (size === "sm") return "pr-2";
  if (size === "md") return "pr-2.5";
  if (size === "lg") return "pr-3";

  return "pr-2";
};

const getHorizontalPadding = (size: (typeof sizes)[number]) => {
  if (size === "sm") {
    return "py-0.5";
  }
  if (size === "md") {
    return "py-0.5";
  }
  if (size === "lg") {
    return "py-1";
  }
  return "py-1";
};

interface IBadgeProps {
  name: string;
  color?: (typeof badgeColors)[number];
  size?: (typeof sizes)[number];
  maxWidth?: number;
  defaultIcon?: boolean;
  leftIcon?: ReactNode;
  onClose?: () => void;
}

export const BadgeV2 = ({
  name,
  color,
  size = "sm",
  maxWidth = 8,
  defaultIcon,
  leftIcon,
  onClose,
}: IBadgeProps) => {
  // If color is not selected, get color from text's hash
  const chooseColor = useMemo(() => {
    if (color) return color;
    const hash = Array.from(name).reduce(
      (prev, cur) => prev + cur.charCodeAt(0),
      0
    );

    return badgeColors[hash % badgeColors.length];
  }, [name, color]);

  return (
    <div
      style={{ maxWidth: `${maxWidth}rem` }}
      className={`h-fit w-fit flex items-center gap-1 rounded-full font-medium ${
        size === "sm" ? "text-xs" : "text-sm"
      } ${getLeftPadding(size, leftIcon ? "icon" : defaultIcon ? "default" : null)} ${getRightPadding(size, onClose ? "default" : null)} ${getHorizontalPadding(size)} border ${tailwindColorClasses[chooseColor]}`}
    >
      {leftIcon ? (
        leftIcon
      ) : defaultIcon ? (
        <Tag01 className={`h-3 w-3 min-w-3 ${tagColorClasses[chooseColor]}`} />
      ) : null}
      <Typography.Text
        ellipsis={{
          tooltip: {
            color: TAILWIND_COLORS["gray-v2-900"],
            title: (
              <p className="text-white text-xs font-semibold text-center">
                {name}
              </p>
            ),
          },
        }}
      >
        {name}
      </Typography.Text>
      {onClose ? (
        <XClose
          className={`h-3 w-3 min-w-3 cursor-pointer ${tagColorClasses[chooseColor]}`}
          onClick={onClose}
        />
      ) : null}
    </div>
  );
};
